import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { authApi } from '../../api/endpoints/auth';
import { handleApiError } from '../../api/errorMapping';
import { buildPasswordSchema } from '../../lib/passwordRules';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';
import { useOtpCooldown } from '../../hooks/useOtpCooldown';
import { PasswordRulesChecklist } from './PasswordRulesChecklist';
import { EMAIL_OTP_LENGTH, formatTime, maskEmail, maskPhone } from '../../lib/otp';
import { identifierValueSchema } from '../../lib/identifier';
import { Button, Card, Input } from '@fiado/react-kit';
import { PoolPicker, type PoolPickerOption } from './PoolPicker';

// Cuatro stages: REQUEST pide el email y resuelve los tenants (multipool, igual que el
// login); si hay 2+ candidatos pasamos por PICK para que el usuario elija de qué cuenta
// recupera. El OTP vence en 2 min, así que separamos la validación del código (paso
// rápido) del set de contraseña (con calma) — ver F-2 recuperación de contraseña.
type Stage = 'REQUEST' | 'PICK' | 'VERIFY_OTP' | 'SET_PASSWORD';

/** Shape estable del form de set-password (el schema es dinámico por policy, el shape no cambia). */
interface SetPasswordValues {
    newPassword: string;
    confirmPassword: string;
}

// DEC-RBAC-080: el paso REQUEST acepta email O teléfono E.164 vía `identifier` (misma regla que
// el login, `identifierValueSchema` compartido). Sólo cambia aquí el label, la validación y el
// body enviado (`{ identifier, email }` como puente al backend, que lee `identifier ?? email`).
const requestSchema = z.object({ identifier: identifierValueSchema });
// El OTP por correo es de 4 dígitos (igual que el OTP del MFA por email — messages-business).
const verifyOtpSchema = z.object({ code: z.string().regex(/^\d{4}$/) });

export function ForgotPasswordForm() {
    const { t } = useTranslation(['auth']);
    const navigate = useNavigate();
    const { policy } = usePasswordPolicy();
    // La contraseña real la valida la política nativa del pool Cognito; el front la refleja desde
    // `GET /public/password-policy` (DEC-RBAC-052, con fallback). `confirmPassword` es del cliente.
    const setPasswordSchema = useMemo(
        () =>
            z
                .object({
                    newPassword: buildPasswordSchema(policy),
                    confirmPassword: z.string(),
                })
                .refine((d) => d.newPassword === d.confirmPassword, {
                    message: 'Las contraseñas no coinciden',
                    path: ['confirmPassword'],
                }),
        [policy],
    );
    const [stage, setStage] = useState<Stage>('REQUEST');
    // identifier (email o teléfono, DEC-RBAC-080), tenantId y resetToken se preservan entre stages:
    // el identifier y el tenantId los necesitan los 3 pasos del recovery (forgotPassword → verifyOtp
    // → confirm), el resetToken lo produce VERIFY_OTP y lo consume SET_PASSWORD. tenantId='' significa
    // "el identificador resolvió a 0 ó 1 tenant sin necesidad de picker" → no se manda al backend.
    const [identifier, setIdentifier] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [resetToken, setResetToken] = useState('');
    // Candidatos a elegir cuando el email es multipool (2+ tenants).
    const [pickOptions, setPickOptions] = useState<PoolPickerOption[]>([]);
    // tenantId solo viaja al backend si el email resolvió a un tenant concreto (multipool
    // o single). Centralizado para no repetir el spread condicional en cada endpoint.
    const withTenant = (tid: string): { tenantId?: string } => (tid ? { tenantId: tid } : {});
    // Cooldown de reenvío refresh-proof (DEC-RBAC-056): guarda el timestamp de envío en
    // sessionStorage y computa el restante; el relojito sobrevive un refresh. `markSent()` lo
    // arranca/reinicia al entrar a VERIFY_OTP y con cada reenvío. flowKey propio para no pisar
    // el cooldown del login/self-register.
    const { secondsLeft, canResend, markSent } = useOtpCooldown('forgot-password');

    const requestForm = useForm<z.infer<typeof requestSchema>>({
        resolver: zodResolver(requestSchema),
        defaultValues: { identifier: '' },
    });
    const verifyOtpForm = useForm<z.infer<typeof verifyOtpSchema>>({
        resolver: zodResolver(verifyOtpSchema),
        defaultValues: { code: '' },
    });
    const setPasswordForm = useForm<SetPasswordValues>({
        resolver: zodResolver(setPasswordSchema),
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    // Dispara el request de reset (paso 1 del recovery) con el tenant ya resuelto y avanza
    // a VERIFY_OTP. Lo llaman tanto el flujo single-tenant (directo tras resolve) como el
    // multipool (tras elegir en el picker).
    const requestMut = useMutation({
        mutationFn: ({ id, tid }: { id: string; tid: string }) =>
            authApi.forgotPassword({ identifier: id, email: id, ...withTenant(tid) }),
        onSuccess: (_resp, { id, tid }) => {
            setIdentifier(id);
            setTenantId(tid);
            toast.message(t('auth:forgotPassword.request.generic'));
            // Arranca el cooldown de 2 min al entrar a VERIFY_OTP (refresh-proof).
            markSent();
            setStage('VERIFY_OTP');
        },
        onError: (err) => {
            handleApiError(err);
        },
    });

    // Paso 1 del identifier-first del recovery: resolver los tenants del identificador (email o
    // teléfono, sin password), mismo patrón que el login. Mandamos `identifier` + `email` (mismo
    // valor) como puente: funciona contra el backend actual y el que ya acepta `identifier`.
    // 0 → mensaje genérico (anti-enumeración). 1 → seguimos directo con ese tenant. 2+ → picker.
    const resolveMut = useMutation({
        mutationFn: (id: string) => authApi.resolveTenants({ identifier: id, email: id }),
        onSuccess: (resp, id) => {
            if (resp.tenants.length === 0) {
                // Anti-enumeración: no revelamos si el identificador existe o no (igual que el login).
                toast.message(t('auth:forgotPassword.request.generic'));
                return;
            }
            if (resp.tenants.length === 1) {
                requestMut.mutate({ id, tid: resp.tenants[0].tenantId });
                return;
            }
            setIdentifier(id);
            setPickOptions(resp.tenants);
            setStage('PICK');
        },
        onError: (err) => {
            handleApiError(err);
        },
    });

    // El usuario eligió de qué cuenta recuperar: disparamos el request con ese tenantId.
    const pickTenant = (option: PoolPickerOption): void => {
        requestMut.mutate({ id: identifier, tid: option.tenantId });
    };

    const verifyOtpMut = useMutation({
        mutationFn: (v: z.infer<typeof verifyOtpSchema>) =>
            authApi.verifyPasswordResetOtp({
                identifier,
                email: identifier,
                otpCode: v.code,
                ...withTenant(tenantId),
            }),
        onSuccess: (resp) => {
            setResetToken(resp.resetToken);
            setStage('SET_PASSWORD');
        },
        onError: (err) => {
            // 401 = código inválido/expirado. handleApiError mapea el resto.
            toast.error(t('auth:forgotPassword.verifyOtp.invalid'));
            handleApiError(err);
        },
    });

    const resendMut = useMutation({
        mutationFn: () =>
            authApi.resendConfirmation({
                identifier,
                email: identifier,
                flowType: 'FORGOT_PASSWORD',
                ...withTenant(tenantId),
            }),
        onSuccess: () => {
            toast.message(t('auth:forgotPassword.verifyOtp.resent'));
            // Reinicia el cooldown a 2 min (refresh-proof).
            markSent();
        },
        onError: (err) => {
            handleApiError(err);
        },
    });

    const confirmMut = useMutation({
        mutationFn: (v: SetPasswordValues) =>
            authApi.confirmForgotPassword({
                identifier,
                email: identifier,
                resetToken,
                newPassword: v.newPassword,
                ...withTenant(tenantId),
            }),
        onSuccess: async () => {
            toast.success(t('auth:forgotPassword.setPassword.success'));
            await navigate({ to: '/login' });
        },
        onError: (err) => {
            handleApiError(err);
        },
    });

    const newPassword = useWatch({ control: setPasswordForm.control, name: 'newPassword' });
    // El código en vivo: habilita "Validar" solo con los 4 dígitos completos (como MFA).
    const code = useWatch({ control: verifyOtpForm.control, name: 'code' });

    const backToLogin = (
        <Button
            type="button"
            variant="ghost"
            fullWidth
            leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />}
            onClick={() => {
                void navigate({ to: '/login' });
            }}
        >
            {t('auth:forgotPassword.backToLogin')}
        </Button>
    );

    if (stage === 'REQUEST') {
        return (
            <Card
                className="w-full max-w-md shadow-lg"
                title={t('auth:forgotPassword.request.title')}
                description={t('auth:forgotPassword.request.subtitle')}
            >
                <form
                    onSubmit={(e) => {
                        void requestForm.handleSubmit((v) => {
                            // Paso 1: resolver tenants. resolveMut decide si seguimos directo
                            // (1 tenant → dispara requestMut), mostramos picker (2+) o cortamos
                            // con mensaje genérico (0).
                            setIdentifier(v.identifier);
                            resolveMut.mutate(v.identifier);
                        })(e);
                    }}
                    className="space-y-5"
                    noValidate
                >
                    <Input
                        id="fp-email"
                        type="text"
                        className="w-full"
                        autoComplete="username"
                        placeholder="tu@email.com o +521234567890"
                        label={t('auth:login.identifier')}
                        error={requestForm.formState.errors.identifier?.message}
                        {...requestForm.register('identifier')}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        isLoading={resolveMut.isPending || requestMut.isPending}
                    >
                        {t('auth:forgotPassword.request.submit')}
                    </Button>
                    {backToLogin}
                </form>
            </Card>
        );
    }

    if (stage === 'PICK') {
        return (
            <Card
                className="w-full max-w-md shadow-lg"
                title={t('auth:forgotPassword.request.title')}
            >
                <PoolPicker
                    prompt={t('auth:forgotPassword.pick.prompt')}
                    options={pickOptions}
                    onPick={pickTenant}
                    secondaryLabel={t('auth:forgotPassword.pick.changeEmail')}
                    onSecondary={() => {
                        setPickOptions([]);
                        setStage('REQUEST');
                    }}
                />
            </Card>
        );
    }

    if (stage === 'VERIFY_OTP') {
        return (
            <Card
                className="w-full max-w-md shadow-lg"
                title={t('auth:forgotPassword.verifyOtp.title')}
                description={
                    identifier
                        ? t('auth:forgotPassword.verifyOtp.subtitle', {
                              // Enmascaramos según el tipo: maskEmail filtraría el teléfono completo
                              // (no tiene `@`) → usamos maskPhone para no filtrar PII (DEC-RBAC-080).
                              email: identifier.includes('@')
                                  ? maskEmail(identifier)
                                  : maskPhone(identifier),
                          })
                        : t('auth:forgotPassword.verifyOtp.subtitle', { email: 'tu correo' })
                }
            >
                <form
                    onSubmit={(e) => {
                        void verifyOtpForm.handleSubmit((v) => {
                            verifyOtpMut.mutate(v);
                        })(e);
                    }}
                    className="space-y-5"
                    noValidate
                >
                    <Input
                        id="fp-code"
                        inputMode="numeric"
                        maxLength={EMAIL_OTP_LENGTH}
                        placeholder={'•'.repeat(EMAIL_OTP_LENGTH)}
                        className="w-full"
                        autoComplete="one-time-code"
                        // OTP de un solo campo: autofocus es buen UX.
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        label={t('auth:forgotPassword.verifyOtp.code')}
                        error={verifyOtpForm.formState.errors.code?.message}
                        {...verifyOtpForm.register('code')}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        isLoading={verifyOtpMut.isPending}
                        disabled={code.length !== EMAIL_OTP_LENGTH}
                    >
                        {t('auth:forgotPassword.verifyOtp.submit')}
                    </Button>
                    <div className="text-center text-sm space-y-1">
                        {secondsLeft > 0 ? (
                            <p className="text-muted-foreground">
                                {t('auth:forgotPassword.verifyOtp.resendIn', {
                                    time: formatTime(secondsLeft),
                                })}
                            </p>
                        ) : (
                            <p className="text-muted-foreground">
                                {t('auth:forgotPassword.verifyOtp.noCode')}
                            </p>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            fullWidth
                            disabled={!canResend || resendMut.isPending}
                            onClick={() => {
                                resendMut.mutate();
                            }}
                        >
                            {t('auth:forgotPassword.verifyOtp.resend')}
                        </Button>
                    </div>
                    {backToLogin}
                </form>
            </Card>
        );
    }

    // SET_PASSWORD
    return (
        <Card
            className="w-full max-w-md shadow-lg"
            title={t('auth:forgotPassword.setPassword.title')}
            description={t('auth:forgotPassword.setPassword.subtitle')}
        >
            <form
                onSubmit={(e) => {
                    void setPasswordForm.handleSubmit((v) => {
                        confirmMut.mutate(v);
                    })(e);
                }}
                className="space-y-5"
                noValidate
            >
                <div className="space-y-2">
                    <Input
                        id="fp-newpwd"
                        type="password"
                        className="w-full"
                        autoComplete="new-password"
                        label={t('auth:forgotPassword.setPassword.newPassword')}
                        error={setPasswordForm.formState.errors.newPassword?.message}
                        {...setPasswordForm.register('newPassword')}
                    />
                    <PasswordRulesChecklist value={newPassword} policy={policy} />
                </div>
                <Input
                    id="fp-confirmpwd"
                    type="password"
                    className="w-full"
                    autoComplete="new-password"
                    label={t('auth:forgotPassword.setPassword.confirmPassword')}
                    error={setPasswordForm.formState.errors.confirmPassword?.message}
                    {...setPasswordForm.register('confirmPassword')}
                />
                <Button type="submit" fullWidth isLoading={confirmMut.isPending}>
                    {t('auth:forgotPassword.setPassword.submit')}
                </Button>
                {backToLogin}
            </form>
        </Card>
    );
}
